/**
 * Helper functions for handling the new customer data structure
 */
import { formatPhoneForDisplay } from '@/utils/phone'

/**
 * Signed thumb (preferred) or full photo URL from API customer payload.
 * @param {Object|null|undefined} customer
 * @returns {string|null}
 */
export const getCustomerPhotoThumbSrc = (customer) => {
  if (!customer) return null
  return customer.photo_thumb_url || customer.photo_url || null
}

/**
 * Get the customer's full name from the new data structure
 * @param {Object} customer - The customer object
 * @returns {string} The customer's full name
 */
export const getCustomerFullName = (customer) => {
  if (!customer) return ''

  // Use the backend's full_name attribute if available
  if (customer.full_name) {
    return customer.full_name
  }

  // Fallback to constructing the name from individual fields
  const parts = []
  if (customer.first_name) parts.push(customer.first_name)
  if (customer.last_name) parts.push(customer.last_name)
  if (customer.suffix) parts.push(customer.suffix)

  return parts.length > 0 ? parts.join(' ') : 'Unnamed Customer'
}

/**
 * Get the customer's primary email from the new data structure
 * @param {Object} customer - The customer object
 * @returns {string} The customer's primary email
 */
export const getCustomerPrimaryEmail = (customer) => {
  if (!customer) return ''

  // Check for primary email in the relationship
  if (customer.primary_email?.email) {
    return customer.primary_email.email
  }

  // Check in emails array for primary
  if (customer.emails && customer.emails.length > 0) {
    const primaryEmail = customer.emails.find(email => email.is_primary)
    if (primaryEmail) return primaryEmail.email

    // Fallback to first email
    return customer.emails[0].email
  }

  // Legacy fallback
  return customer.email || ''
}

/**
 * Get the customer's primary phone from the new data structure
 * @param {Object} customer - The customer object
 * @returns {string} The customer's primary phone
 */
export const getCustomerPrimaryPhone = (customer) => {
  if (!customer) return ''
  const defaultCountry = String(customer.country || 'US').toUpperCase()

  // Check for primary phone in the relationship
  if (customer.primary_phone?.phone) {
    return formatPhoneForDisplay(customer.primary_phone.phone, defaultCountry)
  }

  // Check in phones array for primary
  if (customer.phones && customer.phones.length > 0) {
    const primaryPhone = customer.phones.find(phone => phone.is_primary)
    if (primaryPhone) return formatPhoneForDisplay(primaryPhone.phone, defaultCountry)

    // Fallback to first phone
    return formatPhoneForDisplay(customer.phones[0].phone, defaultCountry)
  }

  // Legacy fallback
  return formatPhoneForDisplay(customer.phone || '', defaultCountry)
}

/**
 * Get customer name for display in tables/lists (with fallback to Walk-in Customer)
 * @param {Object} customer - The customer object
 * @param {string} fallback - Fallback text if no customer (default: 'Walk-in Customer')
 * @returns {string} The customer name or fallback
 */
export const getCustomerNameForDisplay = (customer, fallback = 'Walk-in Customer') => {
  if (!customer) return fallback

  const name = getCustomerFullName(customer)
  return name || fallback
}
